/**
 * Persisted preferences for the Advanced Export modal.
 *
 * The modal used to hard-reset every choice to defaults on each open; this module
 * lets the user's export configuration survive switching files and page refreshes
 * by storing it in localStorage. It is the single source of truth for the export
 * default options.
 *
 * Security / robustness notes (localStorage is attacker-controllable — the user, an
 * XSS payload, or a stale build can write anything):
 *  - The load path treats stored data as UNTRUSTED. It validates object-ness, gates on
 *    a schema version, whitelists the `format` enum, strictly coerces booleans, and
 *    builds the returned object FIELD-BY-FIELD from literals. The parsed blob is never
 *    spread, so unexpected/prototype-pollution keys (`__proto__`, `constructor`, a
 *    stray `filename`) can never leak into the export request body.
 *  - Every storage touch (the `localStorage` getter itself, get/set/remove) is wrapped
 *    in try/catch and degrades to defaults — private mode, quota, and disabled storage
 *    never break the export flow. (Same philosophy as the modal's `trackQuietly`.)
 *  - The payload is non-sensitive by construction: a format string + booleans only. No
 *    transcript content, no filename, no PII, no tokens.
 */

export const EXPORT_PREFS_STORAGE_KEY = "audioscribe_export_preferences";
const PREFS_SCHEMA_VERSION = 1;
const VALID_FORMATS = ["clipboard", "txt", "docx", "pdf", "srt", "vtt"];

/**
 * Canonical default export options. Returns a FRESH object (with a fresh nested
 * `transcript`) on every call so callers can mutate the result freely.
 */
export function defaultExportOptions() {
  return {
    format: "txt",
    transcript: {
      include: true,
      showSpeakerNames: true,
      showTimestamps: true,
      combineSpeakerParagraphs: false,
      combineAllParagraphs: false,
      removeBranding: false,
    },
    includeSummary: false,
    includeChat: false,
    includeMedia: false,
  };
}

/**
 * Load persisted export preferences. ALWAYS returns a safe, fully-shaped result:
 * `{ options, moreOptionsOpen }`. Falls back to defaults on any absence, corruption,
 * version mismatch, or storage error. `moreOptionsOpen` is UI state kept OUT of
 * `options` so it can never reach the export request body.
 */
export function loadExportPreferences() {
  const defaults = { options: defaultExportOptions(), moreOptionsOpen: false };

  const raw = safeGetItem(EXPORT_PREFS_STORAGE_KEY);
  if (!raw) return defaults;

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Malformed JSON — ignore and use defaults.
    return defaults;
  }

  if (!isPlainObject(parsed) || parsed.version !== PREFS_SCHEMA_VERSION) {
    return defaults;
  }

  const ui = isPlainObject(parsed.ui) ? parsed.ui : {};
  return {
    options: sanitizeOptions(parsed.options),
    moreOptionsOpen: strictBool(ui.moreOptionsOpen),
  };
}

/**
 * Persist export preferences. Best-effort: never throws. Serializes a WHITELISTED
 * blob (only known option fields + `ui.moreOptionsOpen`), so a field later added to
 * the modal's `options` (e.g. a per-file `filename`) can never accidentally be stored.
 */
export function saveExportPreferences({ options, moreOptionsOpen } = {}) {
  const blob = {
    version: PREFS_SCHEMA_VERSION,
    options: sanitizeOptions(options),
    ui: { moreOptionsOpen: strictBool(moreOptionsOpen) },
  };
  safeSetItem(EXPORT_PREFS_STORAGE_KEY, JSON.stringify(blob));
}

/** Remove the stored preferences (used by the modal's "Reset to defaults"). Best-effort. */
export function clearExportPreferences() {
  try {
    const storage = getStorage();
    if (storage) storage.removeItem(EXPORT_PREFS_STORAGE_KEY);
  } catch {
    // Best-effort: a removal failure must never break the reset action.
  }
}

// --- internals ---------------------------------------------------------------

/** Rebuild the options object field-by-field from (untrusted) input. Never spreads. */
function sanitizeOptions(raw) {
  const source = isPlainObject(raw) ? raw : {};
  const t = isPlainObject(source.transcript) ? source.transcript : {};
  return {
    format: VALID_FORMATS.includes(source.format) ? source.format : "txt",
    transcript: {
      include: strictBool(t.include),
      showSpeakerNames: strictBool(t.showSpeakerNames),
      showTimestamps: strictBool(t.showTimestamps),
      combineSpeakerParagraphs: strictBool(t.combineSpeakerParagraphs),
      combineAllParagraphs: strictBool(t.combineAllParagraphs),
      removeBranding: strictBool(t.removeBranding),
    },
    includeSummary: strictBool(source.includeSummary),
    includeChat: strictBool(source.includeChat),
    includeMedia: strictBool(source.includeMedia),
  };
}

/** Only a real boolean `true` is true; everything else (string, number, undefined) is false. */
function strictBool(value) {
  return value === true;
}

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/** Returns the localStorage object, or null if it's unavailable/throws on access. */
function getStorage() {
  try {
    if (typeof window === "undefined") return null;
    return globalThis.localStorage || null;
  } catch {
    // Accessing the getter itself can throw in some privacy modes.
    return null;
  }
}

function safeGetItem(key) {
  try {
    const storage = getStorage();
    return storage ? storage.getItem(key) : null;
  } catch {
    return null;
  }
}

function safeSetItem(key, value) {
  try {
    const storage = getStorage();
    if (storage) storage.setItem(key, value);
  } catch {
    // Best-effort: quota / disabled storage must not break the export flow.
  }
}
